import { PDFDocument } from "pdf-lib";
import { message } from "antd";
import { svgToPngBuffer, getCloudPath, getCloudSegments, getTextAnnotationMetrics, getCalloutBox, getCalloutLeaderGeometry, toSvgPoints, normalizeCloudSegment, getCalloutLinkColor, isAutoGeneratedDrawingLink } from "../utils";
import { RFI_TAG_WIDTH, RFI_TAG_HEIGHT, CONNECTION_INNER_RECT_INSET, TEXT_ANNOTATION_CONTENT_INSET, CALLOUT_STROKE_WIDTH } from "../constants";
import { annotationHasLinkedPhoto } from "./linkedPhoto";
import { downloadBlob, withFileExtension } from "../../../utils/downloadUtils";

const escapeSvgText = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Download PDF with annotations embedded
 */
export const downloadPdfWithAnnotations = async (fileUrl, annotations, title, rfiTags = [], drawingNumber = "") => {
  if (!fileUrl) {
    message.error("No PDF file found");
    return;
  }

  try {
    const pdfBytes = await fetch(fileUrl).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(pdfBytes);
    
    for (let i = 0; i < pdfDoc.getPageCount(); i++) {
      const page = pdfDoc.getPage(i);
      const anns = annotations.filter((a) => a.page === i);
      if (anns.length === 0) continue;
      
      const renderScale = 3;
      const svgWidth = page.getWidth() * renderScale;
      const svgHeight = page.getHeight() * renderScale;
      let svg = `<svg width='${svgWidth}' height='${svgHeight}' xmlns='http://www.w3.org/2000/svg'>`;
      
      anns.forEach((ann) => {
        if (ann.type === "pen") {
          let path = ann.path.replace(
            /([ML]) (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)/g,
            (match, cmd, x, y) => {
              return `${cmd} ${parseFloat(x) * renderScale} ${
                parseFloat(y) * renderScale
              }`;
            }
          );
          svg += `<path d='${path}' stroke='${ann.color}' stroke-width='${
            ann.strokeWidth * renderScale
          }' fill='none'/>`;
        } else if (ann.type === "text") {
          const fontSize = ann.fontSize || 16;
          // Omit default boxWidth — legacy annotations must not soft-wrap at 200px.
          const { lines, width, height, lineHeight } = getTextAnnotationMetrics(
            ann.content,
            fontSize,
            ann.boxWidth,
            ann.boxHeight,
          );
          const textX =
            (ann.position.x - width / 2 + TEXT_ANNOTATION_CONTENT_INSET) *
            renderScale;
          const firstLineY =
            (ann.position.y - height / 2) * renderScale +
            (lineHeight * renderScale) / 2 +
            2 * renderScale;
          if (ann.calloutTip) {
            const box = getCalloutBox(ann);
            const strokeW = CALLOUT_STROKE_WIDTH * renderScale;
            svg += `<rect x='${box.left * renderScale}' y='${
              box.top * renderScale
            }' width='${box.width * renderScale}' height='${
              box.height * renderScale
            }' stroke='${ann.color}' stroke-width='${strokeW}' fill='none'/>`;
            const leader = getCalloutLeaderGeometry(
              ann.calloutTip,
              box,
              ann.calloutKnee,
            );
            if (leader) {
              svg += `<polyline points='${toSvgPoints(
                leader.points,
                renderScale,
              )}' stroke='${ann.color}' stroke-width='${strokeW}' stroke-linejoin='round' fill='none'/>`;
              svg += `<polygon points='${toSvgPoints(
                leader.arrow,
                renderScale,
              )}' fill='${ann.color}'/>`;
            }
          }
          svg += `<text x='${textX}' y='${firstLineY}' fill='${
            ann.color
          }' font-size='${fontSize * renderScale}' font-family='Arial' text-anchor='start' dominant-baseline='middle'>`;
          lines.forEach((line, lineIndex) => {
            const dy = lineIndex === 0 ? 0 : lineHeight * renderScale;
            svg += `<tspan x='${textX}' dy='${dy}'>${escapeSvgText(
              line || " ",
            )}</tspan>`;
          });
          svg += `</text>`;
        } else if (ann.type === "shape") {
          if (ann.shapeType === "line") {
            svg += `<line x1='${ann.start.x * renderScale}' y1='${
              ann.start.y * renderScale
            }' x2='${ann.end.x * renderScale}' y2='${
              ann.end.y * renderScale
            }' stroke='${ann.color}' stroke-width='${
              ann.strokeWidth * renderScale
            }'/>`;
          } else if (ann.shapeType === "rectangle") {
            svg += `<rect x='${
              Math.min(ann.start.x, ann.end.x) * renderScale
            }' y='${Math.min(ann.start.y, ann.end.y) * renderScale}' width='${
              Math.abs(ann.end.x - ann.start.x) * renderScale
            }' height='${
              Math.abs(ann.end.y - ann.start.y) * renderScale
            }' stroke='${ann.color}' stroke-width='${
              ann.strokeWidth * renderScale
            }' fill='none'/>`;
          } else if (ann.shapeType === "circle") {
            const left = Math.min(ann.start.x, ann.end.x);
            const top = Math.min(ann.start.y, ann.end.y);
            const width = Math.abs(ann.end.x - ann.start.x);
            const height = Math.abs(ann.end.y - ann.start.y);
            svg += `<ellipse cx='${(left + width / 2) * renderScale}' cy='${
              (top + height / 2) * renderScale
            }' rx='${(width / 2) * renderScale}' ry='${
              (height / 2) * renderScale
            }' stroke='${ann.color}' stroke-width='${
              ann.strokeWidth * renderScale
            }' fill='none'/>`;
          } else if (ann.shapeType === "cloud") {
            getCloudSegments(ann).forEach((segment) => {
              const [x1, y1, x2, y2] = normalizeCloudSegment(segment);
              const sx1 = x1 * renderScale,
                sy1 = y1 * renderScale,
                sx2 = x2 * renderScale,
                sy2 = y2 * renderScale;
              const cloudD = getCloudPath(
                sx1,
                sy1,
                sx2,
                sy2,
                5 * renderScale,
                8 * renderScale,
              );
              svg += `<path d='${cloudD}' stroke='${ann.color}' stroke-width='${
                ann.strokeWidth * renderScale
              }' fill='none'/>`;
            });
          } else if (ann.shapeType === "arrow") {
            const x1 = ann.start.x * renderScale;
            const y1 = ann.start.y * renderScale;
            const x2 = ann.end.x * renderScale;
            const y2 = ann.end.y * renderScale;
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const headlen = 15 * renderScale;
            const arrowX = x2 - headlen * Math.cos(angle - Math.PI / 6);
            const arrowY = y2 - headlen * Math.sin(angle - Math.PI / 6);
            const arrowX2 = x2 - headlen * Math.cos(angle + Math.PI / 6);
            const arrowY2 = y2 - headlen * Math.sin(angle + Math.PI / 6);
            svg += `<line x1='${x1}' y1='${y1}' x2='${x2}' y2='${y2}' stroke='${
              ann.color
            }' stroke-width='${ann.strokeWidth * renderScale}'/>`;
            svg += `<polygon points='${x2},${y2} ${arrowX},${arrowY} ${arrowX2},${arrowY2}' fill='${ann.color}'/>`;
          } else if (ann.shapeType === "polygon") {
            const midX = ((ann.start.x + ann.end.x) / 2) * renderScale;
            const x1 = ann.start.x * renderScale;
            const y1 = ann.start.y * renderScale;
            const x2 = ann.end.x * renderScale;
            const y2 = ann.end.y * renderScale;
            svg += `<polygon points='${x1},${y2} ${midX},${y1} ${x2},${y2}' stroke='${
              ann.color
            }' stroke-width='${ann.strokeWidth * renderScale}' fill='none'/>`;
          }
        } else if (ann.type === "highlight") {
          svg += `<rect x='${
            Math.min(ann.start.x, ann.end.x) * renderScale
          }' y='${Math.min(ann.start.y, ann.end.y) * renderScale}' width='${
            Math.abs(ann.end.x - ann.start.x) * renderScale
          }' height='${
            Math.abs(ann.end.y - ann.start.y) * renderScale
          }' fill='#ffe066' fill-opacity='0.6' stroke='none'/>`;
        } else if (ann.type === "squiggly") {
          const x1 = ann.start.x * renderScale;
          const x2 = ann.end.x * renderScale;
          const y = ann.start.y * renderScale;
          const length = Math.abs(x2 - x1);
          const step = 8 * renderScale;
          let d = `M${x1},${y}`;
          for (let i = 0; i < length; i += step) {
            const x = x1 + i;
            const yOffset =
              i % (2 * step) === 0 ? 4 * renderScale : -4 * renderScale;
            d += ` Q${x + step / 2},${y + yOffset} ${x + step},${y}`;
          }
          svg += `<path d='${d}' stroke='red' stroke-width='${
            2 * renderScale
          }' fill='none'/>`;
        } else if (ann.type === "connection") {
          const boxX = Math.min(ann.start.x, ann.end.x) * renderScale;
          const boxY = Math.min(ann.start.y, ann.end.y) * renderScale;
          const boxWidth = Math.abs(ann.end.x - ann.start.x) * renderScale;
          const boxHeight = Math.abs(ann.end.y - ann.start.y) * renderScale;
          const innerRectInset = CONNECTION_INNER_RECT_INSET * renderScale;
          const isOcrDrawingLink = isAutoGeneratedDrawingLink(ann);
          const frameInset = isOcrDrawingLink ? 0 : innerRectInset;
          if (isOcrDrawingLink) {
            const calloutColor = getCalloutLinkColor(ann);
            const cx = boxX + boxWidth / 2;
            const cy = boxY + boxHeight / 2;
            const rx = Math.max(boxWidth / 2, 6 * renderScale);
            const ry = Math.max(boxHeight / 2, 6 * renderScale);
            svg += `<ellipse cx='${cx}' cy='${cy}' rx='${rx}' ry='${ry}' fill='none' stroke='${calloutColor}' stroke-width='${1.1 * renderScale}'/>`;
          } else {
          svg += `<rect x='${boxX + frameInset}' y='${boxY + frameInset}' width='${
            boxWidth - 2 * frameInset
          }' height='${
            boxHeight - 2 * frameInset
          }' stroke='#ff4d4f' stroke-width='${1.5 * renderScale}' fill='none'/>`;
          
          // Find RFI tag for this connection
          const rfiTag = rfiTags.find(tag => tag.connectionId === ann.id && tag.page === i);
          const hasPhotoLink = annotationHasLinkedPhoto(ann);
          if (hasPhotoLink) {
            const tagWidth = RFI_TAG_WIDTH * renderScale;
            const tagHeight = RFI_TAG_HEIGHT * renderScale;
            const tagX = boxX + boxWidth - innerRectInset;
            const tagY = boxY + boxHeight - innerRectInset - tagHeight;
            svg += `<rect x='${tagX}' y='${tagY}' width='${tagWidth}' height='${tagHeight}' fill='#ff4d4f' rx='${4 * renderScale}'/>`;
            const fontSize = 11 * renderScale;
            svg += `<text x='${tagX + 6 * renderScale}' y='${tagY + tagHeight / 2}' fill='white' font-size='${fontSize}' font-weight='bold' font-family='Arial' text-anchor='start' dominant-baseline='middle'>Photo</text>`;
          } else if (rfiTag && rfiTag.rfiId) {
            // RFI tag dimensions and position
            const tagWidth = RFI_TAG_WIDTH * renderScale;
            const tagHeight = RFI_TAG_HEIGHT * renderScale;
            const tagX = boxX + boxWidth - innerRectInset;
            const tagY = boxY + boxHeight - innerRectInset - tagHeight;
            
            // Draw RFI tag background
            svg += `<rect x='${tagX}' y='${tagY}' width='${tagWidth}' height='${tagHeight}' fill='#ff4d4f' rx='${4 * renderScale}'/>`;
            
            // Draw link tag text using the same prefixes as the viewer.
            const fontSize = 11 * renderScale;
            const tagText =
              rfiTag.type === "submittal"
                ? rfiTag.rfiNumber
                : rfiTag.type === "punchlist"
                  ? `PL ${rfiTag.rfiNumber}`
                  : `RFI ${rfiTag.rfiNumber}`;
            svg += `<text x='${tagX + 6 * renderScale}' y='${tagY + tagHeight / 2}' fill='white' font-size='${fontSize}' font-weight='bold' font-family='Arial' text-anchor='start' dominant-baseline='middle'>${escapeSvgText(tagText)}</text>`;
          } else if (ann.targetDrawingNumber || ann.targetDrawingId) {
            const tagWidth = RFI_TAG_WIDTH * renderScale;
            const tagHeight = RFI_TAG_HEIGHT * renderScale;
            const tagX = boxX + boxWidth - innerRectInset;
            const tagY = boxY + boxHeight - innerRectInset - tagHeight;
            const fontSize = 11 * renderScale;
            const tagText = ann.targetDrawingNumber || "Sheet";
            svg += `<rect x='${tagX}' y='${tagY}' width='${tagWidth}' height='${tagHeight}' fill='#ff4d4f' rx='${4 * renderScale}'/>`;
            svg += `<text x='${tagX + 6 * renderScale}' y='${tagY + tagHeight / 2}' fill='white' font-size='${fontSize}' font-weight='bold' font-family='Arial' text-anchor='start' dominant-baseline='middle'>${escapeSvgText(tagText)}</text>`;
          }
          }
        }
      });
      
      svg += `</svg>`;
      const pngBytes = await svgToPngBuffer(svg, svgWidth, svgHeight);
      const pngImage = await pdfDoc.embedPng(pngBytes);
      page.drawImage(pngImage, {
        x: 0,
        y: 0,
        width: page.getWidth(),
        height: page.getHeight(),
      });
    }
    
    const newPdfBytes = await pdfDoc.save();
    const blob = new Blob([newPdfBytes], { type: "application/pdf" });
    const baseTitle = title || "document";
    const prefix = drawingNumber ? `${drawingNumber}_` : "";
    downloadBlob(
      blob,
      withFileExtension(`${prefix}${baseTitle}_with_annotations`, "pdf"),
    );
  } catch (err) {
    message.error("Failed to download with annotations: " + err.message);
    throw err;
  }
};

/**
 * Download original PDF without annotations
 */
export const downloadPdfWithoutAnnotations = async (fileUrl, title, drawingNumber = "") => {
  if (!fileUrl) return;
  
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error("Failed to download original PDF.");
    }
    const blob = await response.blob();
    const pdfBlob =
      blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" });
    const prefix = drawingNumber ? `${drawingNumber}_` : "";
    downloadBlob(
      pdfBlob,
      withFileExtension(`${prefix}${title || "document"}`, "pdf"),
    );
  } catch (err) {
    message.error("Failed to download original PDF.");
    throw err;
  }
};
